import { PdfRect } from './pdf-viewer.types';

//REV-21369 — die Geometrie der Markierungen (ADR-0021 E5). Reine Funktionen, ohne DOM und ohne
//pdf.js, damit sie in Node prüfbar sind (`pdf-geometry.unit.spec.ts`).
//
//⚠️ Die EINE Stelle, an der zwischen Bildschirm und gespeicherter Form umgerechnet wird. Gespeichert
//wird normiert auf die UNGEDREHTE Seite, Ursprung oben links — dieselbe Konvention wie
//`ExtractionBbox` (ADR-0012 §7). Die Drehung (`/Rotate`) steckt allein im Viewport von pdf.js; wer
//hier selbst dreht, rechnet sie doppelt.

/** Was die Umrechnung vom pdf.js-Viewport braucht — zwei Methoden, sonst nichts. */
export interface PdfViewportLike {
    convertToPdfPoint(x: number, y: number): number[];
    convertToViewportPoint(x: number, y: number): number[];
}

/** `page.view` von pdf.js: [x0, y0, x1, y1] in PDF-Einheiten, Ursprung UNTEN links. */
export type PdfPageView = readonly [number, number, number, number];

/** Ein Rechteck in CSS-Pixeln relativ zur linken oberen Ecke des Seitenknotens. */
export interface CssBox {
    left: number;
    top: number;
    width: number;
    height: number;
}

/** Obergrenze je Markierung — dieselbe wie im Server (`PDF_ANCHOR_MAX_RECTS`). */
export const PDF_MAX_RECTS = 64;

const round4 = (value: number): number => Math.round(value * 10_000) / 10_000;
const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Bildschirm → gespeicherte Form. `null`, wenn nach dem Runden nichts übrig bleibt (ein Klick ohne
 * Ausdehnung, ein Rechteck ganz ausserhalb der Seite).
 */
export function toNormalisedRect(viewport: PdfViewportLike, view: PdfPageView, box: CssBox): PdfRect | null {
    const [x0, y0, x1, y1] = view;
    const pageWidth = x1 - x0;
    const pageHeight = y1 - y0;
    const [ax, ay] = viewport.convertToPdfPoint(box.left, box.top);
    const [bx, by] = viewport.convertToPdfPoint(box.left + box.width, box.top + box.height);

    // Zwei diagonale Ecken, danach min/max: bei 90°/270° vertauschen sich die Achsen, und genau das
    // fängt min/max ab, ohne dass hier jemand den Drehwinkel kennen muss.
    const minX = clamp01((Math.min(ax, bx) - x0) / pageWidth);
    const maxX = clamp01((Math.max(ax, bx) - x0) / pageWidth);
    const minY = clamp01((y1 - Math.max(ay, by)) / pageHeight);
    const maxY = clamp01((y1 - Math.min(ay, by)) / pageHeight);

    const rect: PdfRect = { x: round4(minX), y: round4(minY), w: round4(maxX - minX), h: round4(maxY - minY) };
    return rect.w > 0 && rect.h > 0 ? rect : null;
}

/** Gespeicherte Form → Bildschirm, für das Zeichnen. Die Umkehrung von {@link toNormalisedRect}. */
export function toCssBox(viewport: PdfViewportLike, view: PdfPageView, rect: PdfRect): CssBox {
    const [x0, , , y1] = view;
    const pageWidth = view[2] - view[0];
    const pageHeight = view[3] - view[1];
    const left = x0 + rect.x * pageWidth;
    const right = x0 + (rect.x + rect.w) * pageWidth;
    const top = y1 - rect.y * pageHeight;
    const bottom = y1 - (rect.y + rect.h) * pageHeight;

    const [ax, ay] = viewport.convertToViewportPoint(left, top);
    const [bx, by] = viewport.convertToViewportPoint(right, bottom);
    return {
        left: Math.min(ax, bx),
        top: Math.min(ay, by),
        width: Math.abs(bx - ax),
        height: Math.abs(by - ay),
    };
}

/**
 * Fasst die Rechtecke einer Textauswahl je ZEILE zusammen.
 *
 * `Range.getClientRects()` liefert ein Rechteck je Textstück — eine Zeile aus zwölf Spans sind zwölf
 * Rechtecke, die einander berühren. Gespeichert wird je Zeile eines; und bleiben es trotzdem mehr als
 * {@link PDF_MAX_RECTS}, wird es EIN umschliessendes Rechteck, statt still Zeilen wegzulassen.
 */
export function mergeLineBoxes(boxes: readonly CssBox[]): CssBox[] {
    const sorted = boxes
        .filter((b) => b.width > 0 && b.height > 0)
        .slice()
        .sort((a, b) => a.top - b.top || a.left - b.left);

    const lines: CssBox[] = [];
    for (const box of sorted) {
        const last = lines.length > 0 ? lines[lines.length - 1] : null;
        const sameLine =
            last !== null &&
            // Mehr als die Hälfte der kleineren Höhe überlappt vertikal ⇒ dieselbe Zeile.
            Math.min(last.top + last.height, box.top + box.height) - Math.max(last.top, box.top) >
                Math.min(last.height, box.height) / 2;
        if (sameLine && last !== null) {
            const left = Math.min(last.left, box.left);
            const top = Math.min(last.top, box.top);
            const right = Math.max(last.left + last.width, box.left + box.width);
            const bottom = Math.max(last.top + last.height, box.top + box.height);
            lines[lines.length - 1] = { left, top, width: right - left, height: bottom - top };
        } else {
            lines.push({ ...box });
        }
    }

    if (lines.length <= PDF_MAX_RECTS) {
        return lines;
    }
    const left = Math.min(...lines.map((b) => b.left));
    const top = Math.min(...lines.map((b) => b.top));
    const right = Math.max(...lines.map((b) => b.left + b.width));
    const bottom = Math.max(...lines.map((b) => b.top + b.height));
    return [{ left, top, width: right - left, height: bottom - top }];
}
